import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe,
  Smartphone,
  Layers,
  Server,
  Palette,
  TrendingUp,
  Video,
  Cloud,
  Megaphone,
  Check,
} from 'lucide-react';
import { PRICING_CONFIG, type ServiceItem } from '@/data/pricing';
import { useEstimator } from './context';
import styles from './ProjectEstimator.module.scss';

const SERVICE_ICONS: Record<string, React.ReactNode> = {
  website: <Globe size={22} />,
  'android-app': <Smartphone size={22} />,
  'android-ios-app': <Layers size={22} />,
  'server-hosting': <Server size={22} />,
  'logo-design': <Palette size={22} />,
  seo: <TrendingUp size={22} />,
  'ugc-video': <Video size={22} />,
  'cloud-solutions': <Cloud size={22} />,
  'digital-marketing': <Megaphone size={22} />,
};

const UGC_QUANTITY_OPTIONS = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 5, label: '5' },
  { value: 10, label: '10+' },
];

export const ServicesStep: React.FC = () => {
  const { selections, toggleService, isServiceSelected, setUgcVideoCount } = useEstimator();

  const handleCardClick = (serviceId: string) => {
    toggleService(serviceId);
  };

  const handleUgcCountClick = (e: React.MouseEvent, count: number) => {
    e.stopPropagation();
    setUgcVideoCount(count);
  };

  const currentUgcCount = selections.ugcVideoCount || 1;
  const ugcSubtotal = currentUgcCount * PRICING_CONFIG.ugcVideoUnitPrice;
  const ugcCountDisplay = currentUgcCount >= 10 ? '10+' : currentUgcCount;

  return (
    <div className={styles.stepContainer}>
      {/* ── Heading Group ───────────────────────────────── */}
      <div className={styles.stepHeadingGroup}>
        <span className={styles.stepEyebrow}>STEP 01</span>
        <h1 className={styles.stepHeading}>WHAT DO YOU NEED?</h1>
        <p className={styles.stepSubheading}>
          Select the services you're interested in to get a quick starting estimate.
        </p>
      </div>

      {/* ── 9 Selectable Service Cards ──────────────────── */}
      <div className={styles.servicesGrid}>
        {PRICING_CONFIG.services.map((service: ServiceItem) => {
          const isSelected = isServiceSelected(service.id);
          const icon = SERVICE_ICONS[service.id];

          return (
            <motion.div
              key={service.id}
              id={`service-card-${service.id}`}
              className={`${styles.serviceCard} ${isSelected ? styles.serviceCardSelected : ''}`}
              onClick={() => handleCardClick(service.id)}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.985 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              role="checkbox"
              aria-checked={isSelected}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  handleCardClick(service.id);
                }
              }}
            >
              {/* Card Top: Icon & Checkbox */}
              <div className={styles.serviceCardTop}>
                <div className={styles.serviceIconWrap}>
                  {icon}
                </div>
                <div
                  className={`${styles.serviceCheck} ${
                    isSelected ? styles.serviceCheckActive : ''
                  }`}
                  aria-hidden="true"
                >
                  {isSelected && <Check size={13} strokeWidth={3} />}
                </div>
              </div>

              {/* Card Body: Title & Price */}
              <div className={styles.serviceCardBody}>
                <h3 className={styles.serviceTitle}>{service.cardTitle}</h3>
                <span className={styles.servicePriceBadge}>{service.priceDisplay}</span>
              </div>

              {/* UGC Video Expanded Controls */}
              {service.id === 'ugc-video' && (
                <AnimatePresence>
                  {isSelected && (
                    <motion.div
                      className={styles.ugcConfigArea}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.22, ease: 'easeInOut' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className={styles.ugcDivider} />
                      <div className={styles.ugcHeader}>
                        <span className={styles.ugcLabel}>HOW MANY VIDEOS?</span>
                        <span className={styles.ugcSubtotal}>
                          {ugcCountDisplay} × ₹2,000 = ₹{ugcSubtotal.toLocaleString('en-IN')}{currentUgcCount >= 10 ? '+' : ''}
                        </span>
                      </div>

                      <div className={styles.ugcPillsList}>
                        {UGC_QUANTITY_OPTIONS.map((opt) => {
                          const isOptionActive = currentUgcCount === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              className={`${styles.ugcPillBtn} ${
                                isOptionActive ? styles.ugcPillBtnActive : ''
                              }`}
                              onClick={(e) => handleUgcCountClick(e, opt.value)}
                              aria-pressed={isOptionActive}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>

                      <p className={styles.ugcNote}>30-second UGC video · ₹2,000 each</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}

              {/* Digital Marketing Expanded Note */}
              {service.id === 'digital-marketing' && (
                <AnimatePresence>
                  {isSelected && (
                    <motion.div
                      className={styles.marketingConfigArea}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.22, ease: 'easeInOut' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className={styles.ugcDivider} />
                      <div className={styles.marketingDetails}>
                        <span className={styles.marketingTitle}>Social Media Handling</span>
                        <span className={styles.marketingMeta}>15 posts per month</span>
                        <span className={styles.marketingRate}>₹10,000 / month</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default ServicesStep;
